/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { When } from "react-if";
import { Redirect, Route as NativeRoute, Switch } from "react-router-dom";
import styled from "styled-components";
import { Container, Spacer, theme } from "@nordcloud/gnui";
import { ErrorFallback, GlobalSpinner } from "~/components";

import { useFeature } from "~/hooks";
import { Footer, Header, MenuBar, SidebarLayout } from "~/layout";

import { NoCustomerAccess } from "~/layout/NoCustomerAccess";
import { ACTIONS, Can, useAccess } from "~/services/auth";

import { useCustomers } from "~/services/customers";
import { RumRoute as Route } from "~/services/datadog";
import { lazyWithRetry } from "~/tools";
import { Page403 } from "~/views/403";
import { Page404 } from "~/views/404";
import { AccountAddPage } from "~/views/accounts/AccountAddPage";
import { AccountDetailsPage } from "~/views/accounts/AccountDetails";
import { AccountEditPage } from "~/views/accounts/AccountEditPage";
import { AccountsPage } from "~/views/accounts/AccountsPage";
import { AddCapacityPage } from "~/views/accounts/AddCapacity";
import { ApplicationsCostAnalysisPage } from "~/views/applicationCostAnalysis/ApplicationsCostAnalysisPage";
import { AddApplicationPage } from "~/views/applications/AddApplicationPage";
import { ApplicationDetailsPage } from "~/views/applications/ApplicationDetailsPage";
import { ApplicationsPage } from "~/views/applications/ApplicationsPage";
import { Archive } from "~/views/archive";
import { BusinessMenu, OrganizationUnitTypes } from "~/views/business";
import {
  BusinessContextsStructure,
  OrganizationUnitPage,
} from "~/views/businessContexts";
import { CloudAccountDetailsPage } from "~/views/cloudAccounts/CloudAccountDetails/CloudAccountDetailsPage";
import { CloudAccountsPage } from "~/views/cloudAccounts/CloudAccountsPage";
import { DriftReportPage } from "~/views/drift/DriftReportPage";
import { EnvironmentDetails } from "~/views/environments/Details/EnvironmentDetails";
import { EstateDetailsPage } from "~/views/estate/EstateDetailsPage";
import { EstatePage } from "~/views/estate/EstatePage";
import { EstateRecordsPage } from "~/views/estate/EstateRecordsPage";
import { ExportPage } from "~/views/estate/ExportPage";
import { AddIntegration } from "~/views/externalIntegrations/AddIntegration";
import { ExternalIntegrationsDetailsPage } from "~/views/externalIntegrations/ExternalIntegrationsDetailsPage";
import { ExternalIntegrationsPage } from "~/views/externalIntegrations/ExternalIntegrationsPage";
import { FinopsKpiPage } from "~/views/finopsKpi";
import { HomePageAdmin, HomePageTeamLeader } from "~/views/home";
import { AddIntegrationPlugin } from "~/views/integrationPlugins/AddIntegrationPlugin";
import { IntegrationPluginsDetailsPage } from "~/views/integrationPlugins/IntegrationPluginsDetails";
import { IntegrationPluginsPage } from "~/views/integrationPlugins/IntegrationPluginsList";
import { NotificationPage } from "~/views/notifications/NotificationPage";
import { EmployeesPage } from "~/views/people";
import {
  AwsReservationsCommitmentsPage,
  AzureReservationsCommitmentsPage,
} from "~/views/reservedInstances";
import {
  CloudWasteSettings,
  CostSplittingRulesPage,
  MappingRulesPage,
  SettingsPage,
  UsersAndPermissionsPage,
} from "~/views/settings";
import { ApiKeysTable } from "~/views/settings/GlobalApiKeys";
import { WhatsNew } from "~/views/whatsnew/Whatsnew";
import { AddWorkflowPage } from "~/views/workflows/AddWorkflow";
import { WorkflowDetailsPage } from "~/views/workflows/WorkflowDetails";
import { WorkflowsPage } from "~/views/workflows/WorkflowsPage";
import { ROUTES } from "./routes";
import { AccessHandler, RequireAccess } from "./utils";

const layoutHorizontalSpacing = theme.spacing.spacing04;

// eslint-disable-next-line import/max-dependencies
const ApiDocsPage = lazyWithRetry(() => import("~/views/api"), "ApiDocsPage");

/* eslint-disable max-lines-per-function, max-lines */

export function GlobalSwitch() {
  const { checkAccess } = useAccess();
  const { shouldShowNoCustomerMessage, isAdminLite } = useCustomers();
  const isCloudAccountsEnabled = useFeature("cloudAccounts");

  return (
    <Container
      fluid
      css={{
        paddingLeft: 0,
        paddingTop: 0,
        paddingRight: layoutHorizontalSpacing,
        width: "100%",
        height: "100%",
      }}
    >
      <ApplicationGrid>
        <HeaderWrapper>
          <Header />
        </HeaderWrapper>
        <MenuBar />
        <ApplicationWrapper>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <React.Suspense fallback={<GlobalSpinner loading />}>
              <When condition={shouldShowNoCustomerMessage}>
                <NoCustomerAccess />
                <Spacer height={theme.spacing.spacing04} />
              </When>
              <Switch>
                <Route exact path={ROUTES.index}>
                  {isAdminLite ? (
                    <Redirect to={ROUTES.costAnalysis.index} />
                  ) : (
                    <Can
                      action={ACTIONS.visitHomepage}
                      fallback={<HomePageTeamLeader />}
                    >
                      <HomePageAdmin />
                    </Can>
                  )}
                </Route>
                <Route
                  exact
                  path={ROUTES.export}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitExport}>
                      <ExportPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.applications.index}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitApplications}>
                      <ApplicationsPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.applications.create}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitApplicationAdd}>
                      <AddApplicationPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.applications.details}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitApplicationDetails}>
                      <ApplicationDetailsPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.applications.environment}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitEnvironmentDetails}>
                      <EnvironmentDetails />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.resource}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitEstateRecordsDetails}>
                      <EstateDetailsPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.estateRecords.index}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitEstateRecords}>
                      <EstatePage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.estateRecords.indexV2}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitEstateRecords}>
                      <EstateRecordsPage />
                    </RequireAccess>
                  )}
                />

                <Route
                  exact
                  path={ROUTES.reservedInstances.index}
                  render={() => (
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitReservations)}
                    >
                      <AwsReservationsCommitmentsPage />
                    </AccessHandler>
                  )}
                />

                <Route
                  exact
                  path={ROUTES.reservedInstances.azureReservation}
                  render={() => (
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitReservations)}
                    >
                      <AzureReservationsCommitmentsPage />
                    </AccessHandler>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.externalIntegrations.index}
                  render={() => (
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitExternalIntegrations)}
                    >
                      <ExternalIntegrationsPage />
                    </AccessHandler>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.externalIntegrations.create}
                  render={() => (
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitExternalIntegrations)}
                    >
                      <AddIntegration />
                    </AccessHandler>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.externalIntegrations.details}
                  render={() => (
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitExternalIntegrations)}
                    >
                      <ExternalIntegrationsDetailsPage />
                    </AccessHandler>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.integrationPlugins.index}
                  render={() => <IntegrationPluginsPage />}
                />
                <Route
                  exact
                  path={ROUTES.integrationPlugins.create}
                  render={() => <AddIntegrationPlugin />}
                />
                <Route
                  exact
                  path={ROUTES.integrationPlugins.details}
                  render={() => <IntegrationPluginsDetailsPage />}
                />
                <Route
                  exact
                  path={ROUTES.workflows.index}
                  render={() => (
                    <Route exact path={ROUTES.workflows.index}>
                      <RequireAccess action={ACTIONS.visitWorkflows}>
                        <WorkflowsPage />
                      </RequireAccess>
                    </Route>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.workflows.create}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitWorkflowAdd}>
                      <AddWorkflowPage />
                    </RequireAccess>
                  )}
                />
                <Route exact path={ROUTES.workflows.details}>
                  <RequireAccess action={ACTIONS.visitWorkflowDetails}>
                    <WorkflowDetailsPage />
                  </RequireAccess>
                </Route>
                <Route
                  exact
                  path={ROUTES.estateRecords.drift}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitDriftReport}>
                      <DriftReportPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.cloudAccounts.index}
                  render={() => (
                    <AccessHandler
                      hasAccess={
                        isCloudAccountsEnabled &&
                        checkAccess(ACTIONS.visitAccounts)
                      }
                    >
                      <CloudAccountsPage />
                    </AccessHandler>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.cloudAccounts.details}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitCloudAccountDetails}>
                      <CloudAccountDetailsPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.accounts.index}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitAccounts}>
                      <AccountsPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={[ROUTES.accounts.add]}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitAccountAdd}>
                      <AccountAddPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.accounts.addBillingData}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitAddBillingData}>
                      <AddCapacityPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.accounts.details}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitAccountDetails}>
                      <AccountDetailsPage />
                    </RequireAccess>
                  )}
                />

                <Route
                  exact
                  path={ROUTES.accounts.edit}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitAccountEdit}>
                      <AccountEditPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.notifications.index}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitNotifications}>
                      <NotificationPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.settings.users}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitUsersPermissions}>
                      <UsersAndPermissionsPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.settings.globalApiKeys}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitGlobalApiKeys}>
                      <ApiKeysTable />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.settings.costSplitting}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitCostSplittingRules}>
                      <CostSplittingRulesPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.settings.mapping}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitMapping}>
                      <MappingRulesPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.settings.cloudWaste}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitSavingsSuggestions}>
                      <CloudWasteSettings />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.settings.employees}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitEmployees}>
                      <EmployeesPage />
                    </RequireAccess>
                  )}
                />
                <Route path={[ROUTES.business.unitTypes]}>
                  <SidebarLayout sidebar={<BusinessMenu />}>
                    <Route
                      exact
                      path={ROUTES.business.unitTypes}
                      render={() => (
                        <RequireAccess action={ACTIONS.visitOrgUnitTypes}>
                          <OrganizationUnitTypes />
                        </RequireAccess>
                      )}
                    />
                  </SidebarLayout>
                </Route>
                <Route
                  exact
                  path={ROUTES.businessContexts.structure}
                  render={() => (
                    <RequireAccess
                      action={ACTIONS.visitBusinessContextStructure}
                    >
                      <BusinessContextsStructure />
                    </RequireAccess>
                  )}
                />

                <Route
                  exact
                  path={[
                    ROUTES.businessContexts.details,
                    ROUTES.businessContexts.detailsSimple,
                  ]}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitBusinessContextDetails}>
                      <OrganizationUnitPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.costAnalysis.index}
                  render={() => (
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitCostAnalysis)}
                    >
                      <ApplicationsCostAnalysisPage />
                    </AccessHandler>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.finopsKpi.index}
                  render={() => (
                    <AccessHandler
                      hasAccess={checkAccess(
                        ACTIONS.visitBusinessContextDetails
                      )}
                    >
                      <FinopsKpiPage />
                    </AccessHandler>
                  )}
                />
                <Route
                  exact
                  path={[
                    ROUTES.settings.employees,
                    ROUTES.settings.reports,
                    ROUTES.settings.index,
                  ]}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitSettings}>
                      <SettingsPage />
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.releaseNotes}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitReleaseNotes}>
                      <WhatsNew />
                    </RequireAccess>
                  )}
                />
                <NativeRoute
                  // Native route is used because of frequent pathname updates inside `ApiDocsPage`, which caused a lot of network requests to datadog
                  exact
                  path={ROUTES.api}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitApiDocs}>
                      <React.Suspense fallback={<GlobalSpinner loading />}>
                        <ApiDocsPage />
                      </React.Suspense>
                    </RequireAccess>
                  )}
                />
                <Route
                  exact
                  path={ROUTES.archive}
                  render={() => (
                    <RequireAccess action={ACTIONS.visitArchive}>
                      <Archive />
                    </RequireAccess>
                  )}
                />
                <Route exact path={ROUTES.accessDenied}>
                  <Page403 />
                </Route>
                <Route path="*">
                  <Page404 />
                </Route>
              </Switch>
            </React.Suspense>
          </ErrorBoundary>
        </ApplicationWrapper>
        <FooterWrapper>
          <Footer />
        </FooterWrapper>
      </ApplicationGrid>
    </Container>
  );
}

// Cache menu bar & layout dimensions
const menuWidth = theme.spacing.spacing07;
const menuHorizontalPadding = theme.spacing.spacing03;

// `1st calc`: we need to add menuHorizontalPadding twice because we have
// PaddingLeft and paddingRight on the menu element

// `2nd calc`: we need to add layoutHorizontalSpacing additionally to mitigate
// Issue with right padding on global application container which is wrongly
// Interpreted by the grid
const ApplicationGrid = styled.div`
  display: grid;
  gap: ${theme.spacing.spacing04} ${layoutHorizontalSpacing};
  grid-template-columns:
    calc(${menuWidth} + 2 * ${menuHorizontalPadding})
    calc(
      100% -
        (
          ${menuWidth} + 2 * ${menuHorizontalPadding} +
            ${layoutHorizontalSpacing}
        )
    );
  grid-template-rows: auto 1fr auto;
  grid-template-areas:
    ". header"
    ". application"
    ". footer";
  width: 100%;
  height: 100%;
`;

const HeaderWrapper = styled.div`
  margin: 0 -${theme.spacing.spacing04} 0 -${layoutHorizontalSpacing};
  grid-area: header;
`;

const ApplicationWrapper = styled.div`
  grid-area: application;
  width: 100%;

  @media screen and (min-width: 2500px) {
    margin: 0 auto;
    max-width: 2500px;
  }
`;

const FooterWrapper = styled.div`
  grid-area: footer;
  padding-top: ${theme.spacing.spacing05};
`;
